"use client"

import { toast } from "sonner"

export interface ToastProps {
  title?: string
  description?: string
  variant?: "default" | "destructive" | "success" | "warning" | "info"
  duration?: number
}

export function useToast() {
  const showToast = ({ title, description, variant = "default", duration = 4000 }: ToastProps) => {
    const message = title || description || ""
    const fullMessage = title && description ? `${title} ${description}` : message

    switch (variant) {
      case "success":
        return toast.success(fullMessage, { duration })
      case "destructive":
        return toast.error(fullMessage, { duration })
      case "warning":
        return toast.warning(fullMessage, { duration })
      case "info":
        return toast.info(fullMessage, { duration })
      default:
        return toast(fullMessage, { duration })
    }
  }

  return {
    toast: showToast,
    success: (message: string, duration?: number) => showToast({ description: message, variant: "success", duration }),
    error: (message: string, duration?: number) =>
      showToast({ description: message, variant: "destructive", duration }),
    warning: (message: string, duration?: number) => showToast({ description: message, variant: "warning", duration }),
    info: (message: string, duration?: number) => showToast({ description: message, variant: "info", duration }),
    promise: toast.promise,
    dismiss: toast.dismiss,
    loading: toast.loading,
  }
}

// Export individual toast functions for convenience
export const { success, error, warning, info, promise, dismiss, loading } = {
  success: (message: string, duration?: number) => toast.success(message, { duration }),
  error: (message: string, duration?: number) => toast.error(message, { duration }),
  warning: (message: string, duration?: number) => toast.warning(message, { duration }),
  info: (message: string, duration?: number) => toast.info(message, { duration }),
  promise: toast.promise,
  dismiss: toast.dismiss,
  loading: toast.loading,
}
